import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Validate purchase return eligibility
export const validatePurchaseReturn = createAsyncThunk(
    'purchaseReturn/validate',
    async ({ purchase_id, items }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_URL}/purchase-returns/validate`,
                { purchase_id, items },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

// Create purchase return
export const createPurchaseReturn = createAsyncThunk(
    'purchaseReturn/create',
    async (returnData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_URL}/purchase-returns`,
                returnData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

// Fetch all purchase returns
export const fetchPurchaseReturns = createAsyncThunk(
    'purchaseReturn/fetchAll',
    async ({ page = 1, limit = 10, status, supplier_id } = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({ page, limit });
            if (status) params.append('status', status);
            if (supplier_id) params.append('supplier_id', supplier_id);

            const response = await axios.get(
                `${API_URL}/purchase-returns?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

// Fetch single purchase return
export const fetchPurchaseReturnById = createAsyncThunk(
    'purchaseReturn/fetchById',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_URL}/purchase-returns/${id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

// Approve purchase return
export const approvePurchaseReturn = createAsyncThunk(
    'purchaseReturn/approve',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(
                `${API_URL}/purchase-returns/${id}/approve`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

// Reject purchase return
export const rejectPurchaseReturn = createAsyncThunk(
    'purchaseReturn/reject',
    async ({ id, rejection_reason }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(
                `${API_URL}/purchase-returns/${id}/reject`,
                { rejection_reason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

const purchaseReturnSlice = createSlice({
    name: 'purchaseReturn',
    initialState: {
        returns: [],
        currentReturn: null,
        validationResult: null,
        loading: false,
        error: null,
        currentPage: 1,
        totalPages: 1,
        total: 0
    },
    reducers: {
        clearValidation: (state) => {
            state.validationResult = null;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Validate
            .addCase(validatePurchaseReturn.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(validatePurchaseReturn.fulfilled, (state, action) => {
                state.loading = false;
                state.validationResult = action.payload;
            })
            .addCase(validatePurchaseReturn.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Validation failed';
            })
            // Create
            .addCase(createPurchaseReturn.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createPurchaseReturn.fulfilled, (state, action) => {
                state.loading = false;
                state.returns.unshift(action.payload.purchaseReturn);
            })
            .addCase(createPurchaseReturn.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Failed to create return';
            })
            // Fetch all
            .addCase(fetchPurchaseReturns.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPurchaseReturns.fulfilled, (state, action) => {
                state.loading = false;
                state.returns = action.payload.returns;
                state.currentPage = action.payload.currentPage;
                state.totalPages = action.payload.totalPages;
                state.total = action.payload.total;
            })
            .addCase(fetchPurchaseReturns.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Failed to fetch returns';
            })
            // Fetch by ID
            .addCase(fetchPurchaseReturnById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPurchaseReturnById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentReturn = action.payload;
            })
            .addCase(fetchPurchaseReturnById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Failed to fetch return';
            })
            // Approve
            .addCase(approvePurchaseReturn.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(approvePurchaseReturn.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.returns.findIndex(r => r._id === action.payload.purchaseReturn._id);
                if (index !== -1) {
                    state.returns[index] = action.payload.purchaseReturn;
                }
            })
            .addCase(approvePurchaseReturn.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Failed to approve return';
            })
            // Reject
            .addCase(rejectPurchaseReturn.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(rejectPurchaseReturn.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.returns.findIndex(r => r._id === action.payload.purchaseReturn._id);
                if (index !== -1) {
                    state.returns[index] = action.payload.purchaseReturn;
                }
            })
            .addCase(rejectPurchaseReturn.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Failed to reject return';
            });
    }
});

export const { clearValidation, clearError } = purchaseReturnSlice.actions;
export default purchaseReturnSlice.reducer;
